import React, { useState, useEffect, useRef, useMemo } from "react"

import { ErrorMessage, Field } from "formik"
import { Autocomplete, Box, Button, CircularProgress, MenuItem, Select, Stack } from "@mui/material"
import IconButton from "@mui/material/IconButton"
import ClearIcon from "@mui/icons-material/Clear"
import { cloneDeep, debounce, isEqual, uniqBy } from "lodash"
import _cloneDeep from "lodash/cloneDeep"

import { COLORS, findObjectByPathValue, roundNumber } from "../../../../utils"
import { PRODUCTION_STEPS_COL_WIDTHS, COLS_OFFSETS } from "../../../../utils/constant"
import {
  getEmptyStepComponent,
  getEmptyPriorStepSupplierItem,
  resetStepComponent,
  recalculateCostValues,
  updateCopyStepComponentCost,
  loopSupplierItemOnPriorSteps,
  updateAllRecipesFieldsOnChange,
  getProductionStepPointerObj,
  updatePriorStepsInUlteriorComponents,
  // updatePriorStepSupplierItemsInUlteriorComponents,  KFC-2429 - To be added
  getSupplierItemPieceUnitWeight
} from "../../../../utils/recipes"

import { searchSupplierItemsAutocomplete } from "../../../../parseManager/suppliers/supplierItems/parseSupplierItemManager"
import { convertGramsIntoKilos, convertKilosIntoGrams } from "../../../../utils/ressources"
import RemoveColumn from "../RemoveColumn"
import { StyledErrorMessage, StyledProductionStepTextField, StyledStepBodyCell, StyledStepFirstBodyColumn, StyledStepText, StyledPieceStepInfo } from "../StyledSectionComponents"
import { v4 as uuidv4 } from "uuid"
import { removeStepComponent } from "../../../../utils/stepComponentUtils"
import { getSectionOutputsData, updateRecipeOnChangeSectionOutput } from "../../../../utils/sectionUtils"
const widths = PRODUCTION_STEPS_COL_WIDTHS

const getOptionLabel = (option, stepNumbers) => {
  const currentStepNumber = stepNumbers && option.data && stepNumbers[option.data.index]
  // display step root index if it's a prior steps
  if (option.categoryId === "PRIOR_STEPS") {
    return currentStepNumber + ". " + option.name
  }
  return option.name
}

const FormikTextField = ({ field, ...props }) => (
  <StyledProductionStepTextField {...field} {...props} />
)

const autocompleteSx = {
  textField: {
    "& .MuiInput-input": {
      cursor: "pointer"
    }
  }
}

const FormikAutocomplete = ({ form, loading, field, readOnly = false, isHovered = false, supplierItems, priorSteps, priorSectionOutputs, category, ...props }) => {
  const { value } = field
  const { handleChange } = form

  const currentValue = value && category === "SUPPLIER_ITEM" ?
    supplierItems.find(supplierItem => supplierItem.objectId === value.id) :
    value && category === "SECTION_OUTPUTS" ?
      priorSectionOutputs.find(psO => psO.data.index === value.index) :
      value && priorSteps.find(priorStep => priorStep.data.objectId === value.id)

  return (
    <Autocomplete
      {...props}
      sx={{ flex: 1, pointer: "cursor" }}
      options={props.options}
      // current values is found from supplierItems
      // values from the searched supplierItems options
      // so we need value so that the input is not empty after selection
      value={currentValue || value}
      groupBy={(option) => option.category}
      renderInput={(params) => (
        <StyledProductionStepTextField
          {...params}
          isHovered={isHovered}
          variant="standard"
          fullWidth
          inputProps={{ ...params.inputProps, readOnly }}
          sx={readOnly ? autocompleteSx.textField : null}
          onChange={handleChange}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
    />
  )
}

export const FormikEditableStepSelect = ({ field, children, readOnly = false, ...props }) => {
  const { name, value } = field

  return (
    <Select
      {...props}
      name={name}
      value={value && value.objectId}
      variant="standard"
      isClearable={true}
      renderInput={(params) => (
        <StyledProductionStepTextField
          {...params}
          variant="standard"
          fullWidth
          inputProps={{ ...params.inputProps, readOnly }}
          sx={readOnly ? autocompleteSx.textField : null}
        />
      )}
    >
      {children}
    </Select>
  )
}

const getTransformationModeAvailable = (supplierItem, transformationModes, parentStep) => {
  return supplierItem && transformationModes && supplierItem.transformationModes ? (supplierItem.transformationModes.filter(transformation =>
    transformation?.transformationMode && transformationModes.find(t => t.objectId === transformation.transformationMode.objectId) && transformation.transformationMode.transformationType === parentStep.transformation
  )) : []
}

const EditableStepComponent = ({
  stepComponent,
  stepComponents,
  steps,
  parentStep,
  transformationModes,
  indexComponent,
  indexStep,
  sectionIndex,
  supplierItems = [],
  isHover,
  hasError,
  setFieldValue,
  fromRecipe,
  recipe,
  section,
  stepNumbers
}) => {
  const [supplierItemsOptions, setSupplierItemsOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [priorSteps, setPriorSteps] = useState([])
  const [priorSectionOutputs, setPriorSectionOutputs] = useState([])

  const _stopPropagation = (event) => event && event.stopPropagation()

  const _calculateSectionOutputs = () => {
    const priorSectionOutputs = []
    // get sectionOutputs from recipe.sections until the current section
    recipe && recipe.sections.forEach((recipeSection, index) => {
      if (index < sectionIndex) {
        const sectionOutputs = getSectionOutputsData(recipeSection).map((sectionOutput) => {
          return {
            ...sectionOutput,
            netWeight: sectionOutput.ingredients.reduce((sum, ingredient) => sum + (ingredient.netWeight || 0), 0),
            grossWeight: sectionOutput.ingredients.reduce((sum, ingredient) => sum + (ingredient.netWeight || 0), 0),
            stepComponents: sectionOutput.ingredients.map(ingredient => ({
              ...ingredient,
              netWeight: ingredient.netWeight || 0,
              grossWeight: ingredient.netWeight || 0,
              transformationMode: null,
              transformationRate: 100,
              supplierItem: ingredient.supplierItem || null,
            }))
          }
        })
        sectionOutputs.forEach((sectionOutput, soIndex) => {
          const copySectionOutput = _cloneDeep(sectionOutput)
          /** COPY STEP COMPONENT COSTS CALCULATION */
          for (let copyStepComponent of copySectionOutput.stepComponents) {
            copyStepComponent = updateCopyStepComponentCost(copyStepComponent)
            /** PRIOR STEP COSTS CALCULATION */
            if (copyStepComponent.priorSteps && copyStepComponent.priorSteps.stepComponents && copyStepComponent.priorSteps.stepComponents.length > 0) {
              loopSupplierItemOnPriorSteps(copyStepComponent.priorSteps.stepComponents)
            }
          }
          const sectionOutputObj = {
            name: copySectionOutput.name ?? `${recipeSection.name} - partie ${soIndex + 1}`,
            value: copySectionOutput.index,
            index: copySectionOutput.index,
            data: { ...copySectionOutput, name: copySectionOutput.name ?? `${recipeSection.name} - partie ${soIndex + 1}` },
            category: "Résultat de sortie de section",
            categoryId: "SECTION_OUTPUTS"
          }
          priorSectionOutputs.push(sectionOutputObj)
        })
      }
    })
    const alreadyUsedPriorSectionOutputs = priorSectionOutputs.filter((priorSectionOutput) => {
      const usedInSection = findObjectByPathValue(recipe, "stepComponents.sectionOutput.index", priorSectionOutput.index, { tree: true, recursive: true })

      return usedInSection.length
    })

    return {
      priorSectionOutputs: priorSectionOutputs.filter(psO => !alreadyUsedPriorSectionOutputs.map(({ index }) => index).includes(psO.index)), // section outputs not used in any step
      alreadyUsedPriorSectionOutputs
    }
  }

  const _calculatePriorSteps = (fromRecipe) => {
    const priorSteps = []
    let found = false

    steps.forEach((currentStep) => {
      let step = getProductionStepPointerObj(currentStep, fromRecipe)
      // if step is reusable we need to be able to select the last productionStep as a prior step
      // @see KFC 1852
      if (step.isReusable && step.productionSteps && !!step.productionSteps.length) {
        const temp = step
        const stepRef = step.productionSteps[step.productionSteps.length - 1]
        step = { ...stepRef, grossWeight: temp.grossWeight, netWeight: temp.netWeight, cost: temp.cost }
      }

      if (step.stepComponents && step.stepComponents.length > 0) {
        if (!found && !step.stepComponents.find(currentStepComponent => currentStepComponent.index === stepComponent.index)) {
          step.category = "PRIOR_STEPS"
          const copyStep = _cloneDeep(step)
          /** COPY STEP COMPONENT COSTS CALCULATION */
          for (let copyStepComponent of copyStep.stepComponents) {
            copyStepComponent = updateCopyStepComponentCost(copyStepComponent)
            /** PRIOR STEP OR SECTION OUTPUT COSTS CALCULATION */
            const type = copyStepComponent.priorSteps ? "priorSteps" : copyStepComponent.sectionOutput ? "sectionOutput" : null
            if (type && copyStepComponent[type].stepComponents && copyStepComponent[type].stepComponents.length > 0) {
              loopSupplierItemOnPriorSteps(copyStepComponent[type].stepComponents)
            }
          }
          priorSteps.push({
            name: copyStep.name,
            value: copyStep.objectId ? copyStep.objectId : uuidv4(),
            index: copyStep.index,
            data: copyStep,
            category: "Résultat d'étapes antérieures",
            categoryId: "PRIOR_STEPS",
            stepRootIndex: copyStep.stepRootIndex
          })

        } else {
          found = true
          for (const stepComponent of step.stepComponents) {
            const currentStep = priorSteps.find(priorStep => priorStep.data?.index === stepComponent.priorSteps?.index)
            if (currentStep) {
              stepComponent.priorSteps.name = currentStep.name
            }
          }
        }
      }
    })

    // remove from options priorSteps already used as prior steps in another step so that they can't be selected twice
    const filteredPriorSteps = priorSteps.filter((priorStep) => {
      const alreadyUsed = steps.some((step) => {
        const stepRef = fromRecipe ? step.step : step
        if (stepRef.productionSteps) {
          const productionSteps = stepRef.productionSteps
          return productionSteps.some((productionStep) => {
            const stepComponents = productionStep.stepComponents
            return stepComponents && stepComponents.some((stepComponent) => {
              return stepComponent.priorSteps && stepComponent.priorSteps.index === priorStep.index
            })
          })
        } else if (stepRef.stepComponents) { // reusable
          const stepComponents = stepRef.stepComponents
          return stepComponents.some((stepComponent) => {
            return stepComponent.priorSteps && stepComponent.priorSteps.index === priorStep.index
          })
        }
      })
      return !alreadyUsed
    })

    const alreadyUsedPriorSteps = priorSteps.filter((priorStep) => {
      return !filteredPriorSteps.some((filteredPriorStep) => filteredPriorStep.index === priorStep.index)
    })

    return {
      priorSteps: filteredPriorSteps,
      alreadyUsedPriorSteps
    }
  }

  useEffect(() => {
    const { priorSectionOutputs } = _calculateSectionOutputs()
    setPriorSectionOutputs(priorSectionOutputs)
    const { priorSteps, alreadyUsedPriorSteps } = _calculatePriorSteps(fromRecipe)
    setPriorSteps(priorSteps)
    const updatedSupplierItemsOptions = supplierItemsOptions.filter((option) => !alreadyUsedPriorSteps.some((alreadyUsedPriorStep) => alreadyUsedPriorStep.index === option.data.index))
    const options = [...priorSteps, ...priorSectionOutputs, ...updatedSupplierItemsOptions]
    const optionsDeduplicated = options.filter((obj, index) => options.findIndex((item) => item.value === obj.value && item.categoryId === obj.categoryId) === index)
    setSupplierItemsOptions(uniqBy(optionsDeduplicated, (option) => option.index))
  }, [steps, recipe, recipe?.sections])

  let supplierItem = stepComponent.supplierItem

  const _updatePriorStepsInUlteriorComponents = (method = "CREATE", modifiedStepComponent = null) => { // could be delete or create
    const { name } = parentStep // KFC-2429 - To be removed
    // const stepComponentIndexes = parentStep.stepComponents.map(stepComponent => stepComponent.index) KFC-2429 - To be added
    updatePriorStepsInUlteriorComponents({
      currentStepComponent: modifiedStepComponent,
      currentStepName: name, // KFC-2429 - To be removed
      // currentStepComponentIndexes: stepComponentIndexes, KFC-2429 - To be added
      currentStepIndex: indexStep,
      allSteps: steps,
      section,
      recipe,
      method,
      isProductionSteps: !!fromRecipe
    })
  }

  // const _updatePriorStepSupplierItemsInUlteriorComponents = (method = "CREATE", supplierItemInput = null, stepComponentIndex = null) => {
  //   updatePriorStepSupplierItemsInUlteriorComponents({
  //     supplierItemInput: supplierItemInput,
  //     currentStepIndex: indexStep,
  //     allSteps: steps,
  //     method,
  //     isProductionSteps: !!fromRecipe,
  //     stepComponentIndex
  //   })
  // } KFC-2429 - To be added

  const _addStepComponent = (index, event = null) => {
    const newStepComponents = [...stepComponents]

    const emptyComponent = getEmptyStepComponent()

    newStepComponents.splice(index + 1, 0, emptyComponent)

    if (fromRecipe) {
      setFieldValue(
        `sections[${sectionIndex}].productionSteps[${indexStep}].step.stepComponents`,
        newStepComponents
      )
    } else {
      setFieldValue(
        `productionSteps[${indexStep}].stepComponents`,
        newStepComponents
      )
    }

    // _updatePriorStepSupplierItemsInUlteriorComponents("CREATE", getEmptyPriorStepSupplierItem(emptyComponent.index)) KFC-2429 - To be added
    _updatePriorStepsInUlteriorComponents("CREATE", emptyComponent)

    _stopPropagation(event)
  }

  const _removeStepComponent = (index, event) => {

    removeStepComponent(recipe, stepComponent, setFieldValue, stepComponents, {
      fromRecipe,
      sectionIndex,
      indexStep,
      stepComponent,
      section,
      steps,
      parentStep,
      index
    })

    _stopPropagation(event)
  }

  const clearStepComponentTransformationMode = () => {
    stepComponent.transformationMode = null
    stepComponent.transformRate = 100
    stepComponent.grossWeight = stepComponent.netWeight
    setFieldValue(stepComponentTransformationModeName, null)
    setFieldValue(stepComponentTransformRateName, 100)
    stepComponent = recalculateCostValues(stepComponent)

    updateAllRecipesFieldsOnChange({
      recipe,
      section,
      steps,
      parentStep,
      currentStepComponentChanged: stepComponent,
      isProductionSteps: fromRecipe
    })
  }

  const transformationModesOptions = useMemo(() => {
    return getTransformationModeAvailable(stepComponent.supplierItem, transformationModes, parentStep)
  }, [stepComponent, stepComponent.supplierItem?.objectId, transformationModes, parentStep, parentStep?.objectId])


  const handleChangeSupplierItemOrPriorStepAutocomplete = (newValue) => { //, indexComponent = null) => { KFC-2429 - To be added

    stepComponent = resetStepComponent(stepComponent)
    stepComponent.priorSteps = null
    stepComponent.supplierItem = null
    stepComponent.sectionOutput = null

    newValue.data.id = newValue.data.objectId === undefined ? uuidv4() : newValue.data.objectId

    const stepComponentFieldName = fromRecipe
      ? `sections[${sectionIndex}].productionSteps[${indexStep}].step.stepComponents[${indexComponent}]`
      : `productionSteps[${indexStep}].stepComponents[${indexComponent}]`

    const productionStepFieldName = fromRecipe
      ? `sections[${sectionIndex}].productionSteps[${indexStep}]`
      : `productionSteps[${indexStep}]`

    if (newValue.categoryId === "PRIOR_STEPS" || newValue.categoryId === "SECTION_OUTPUTS") {
      const priorStep = newValue.data
      const categoryKey = newValue.categoryId === "PRIOR_STEPS" ? "priorSteps" : "sectionOutput"
      stepComponent[categoryKey] = priorStep

      // Compute the stepComponent net/gross weight
      stepComponent.netWeight = priorStep.netWeight || priorStep.stepComponents?.reduce((sum, comp) => sum + (comp.netWeight || 0), 0) || 0
      stepComponent = recalculateCostValues(stepComponent)
      // update step component
      setFieldValue(stepComponentFieldName, stepComponent)

      // Compute the root productionStep net/gross weight
      const totalNetWeight = parentStep.stepComponents.reduce((sum, comp) => sum + (comp.netWeight || 0), 0)
      const totalGrossWeight = parentStep.stepComponents.reduce((sum, comp) => sum + (comp.grossWeight || 0), 0)
      // Update the productionStep
      setFieldValue(`${productionStepFieldName}.netWeight`, totalNetWeight)
      setFieldValue(`${productionStepFieldName}.grossWeight`, totalGrossWeight)
      setFieldValue(`${productionStepFieldName}.step.netWeight`, totalNetWeight)
      setFieldValue(`${productionStepFieldName}.step.grossWeight`, totalGrossWeight)
      if (newValue.categoryId === "SECTION_OUTPUTS") {
        stepComponent.sectionOutput.stepComponents.forEach(sC => {
          updateAllRecipesFieldsOnChange({
            recipe,
            section,
            steps,
            parentStep,
            currentStepComponentChanged: sC,
            isProductionSteps: !!fromRecipe,
          })
        })
        updateAllRecipesFieldsOnChange({
          recipe,
          section,
          steps,
          parentStep,
          currentStepComponentChanged: stepComponent,
          isProductionSteps: fromRecipe,
        })
        if (fromRecipe) {

          setFieldValue(`sections[${sectionIndex}].netWeight`, section.netWeight)
          setFieldValue(`sections[${sectionIndex}].grossWeight`, section.grossWeight)
        }
        // handleChangeNetWeight()
        setFieldValue(stepComponentFieldName, stepComponent)
        return
      }
      // Add the new supplier items to the priorStepSupplierItems list
      stepComponent.priorStepSupplierItems = []
      newValue.data.stepComponents.forEach((subStepComponent) => {
        stepComponent.priorStepSupplierItems.push(getEmptyPriorStepSupplierItem(subStepComponent.index, subStepComponent.supplierItem))
      })
      return
    } else if (section.sectionOutputs && section.sectionOutputs.length > 0 && section.sectionOutputs.find(so => so.type === "ingredient")) {

      const newSectionOutputs = section.sectionOutputs.map(so => {
        return {
          ...so,
          ingredients: so.ingredients.map(sC => {
            if (sC.stepComponentIndex === stepComponent.index) {
              sC.supplierItem = newValue.data
            }
            return sC
          })
        }
      })
      setFieldValue(`sections.${sectionIndex}.sectionOutputs`, newSectionOutputs)
      updateRecipeOnChangeSectionOutput({
        ...recipe,
        sections: recipe.sections.map((sct, sctIndex) => sctIndex === sectionIndex ? { ...section, sectionOutputs: newSectionOutputs } : sct)
      },
        {
          ...recipe.sections[sectionIndex],
          sectionOutputs: newSectionOutputs
        }
      )
      // console.log("looking for section output supplier item", section.sectionOutputs, newValue.data, stepComponent, newSectionOutputs)
    }

    supplierItem = newValue.data
    stepComponent.supplierItem = supplierItem
    stepComponent = recalculateCostValues(stepComponent)
    setFieldValue(stepComponentFieldName, stepComponent)

    // update ulterior step (if any) with the new supplierItem
    // _updatePriorStepSupplierItemsInUlteriorComponents("UPDATE", supplierItem, stepComponent.index) KFC-2429 - To be added
    _updatePriorStepsInUlteriorComponents("UPDATE", stepComponent)
  }

  const searchingSupplier = useRef(
    debounce(async (event) => {
      if (event && event.type === "change") {
        setLoading(true)
        const supplierItemsFilters = { type: "RAW_MATERIAL" }
        const search = event.target.value
        const supplierItems = await searchSupplierItemsAutocomplete(search, supplierItemsFilters)
        const { priorSteps } = _calculatePriorSteps()
        const { priorSectionOutputs } = _calculateSectionOutputs()
        const result = [...priorSteps, ...priorSectionOutputs, ...supplierItems.map((supplierItem) => ({
          name: supplierItem.name.toLowerCase(),
          value: supplierItem.objectId,
          index: supplierItem.index,
          data: supplierItem,
          category: "Articles fournisseurs",
          categoryId: "SUPPLIER_ITEM"
        }
        ))]

        setSupplierItemsOptions(result)
        setLoading(false)
      }
    }, 700)
  ).current

  const handleChangeNetWeight = (event) => {
    const newParentStep = cloneDeep(parentStep)
    let newStepComponent = { ...stepComponent, netWeight: convertGramsIntoKilos(+event.target.value) }
    newStepComponent = recalculateCostValues(newStepComponent)

    // update step component
    newParentStep.stepComponents[indexComponent] = newStepComponent

    if (fromRecipe) {
      // update parent step and steps
      const index = steps.findIndex(step => isEqual(step.step, parentStep))
      steps[index].step = newParentStep
    }
    else {
      // update parent step and steps
      const index = steps.indexOf(parentStep)
      steps[index] = newParentStep
    }
    // compute recipe data with new parent step and stepcompoent and retrieve total gross weight
    const { reusableStepTotalGrossWeight } = updateAllRecipesFieldsOnChange({
      recipe,
      section,
      steps,
      parentStep,
      currentStepComponentChanged: newStepComponent,
      isProductionSteps: !!fromRecipe,
    })

    // recipe
    if (fromRecipe) {
      setFieldValue(
        `sections[${sectionIndex}].productionSteps[${indexStep}].step.stepComponents[${indexComponent}]`,
        newStepComponent
      )
    }
    else {
      // reusable step BO
      setFieldValue("grossWeight", reusableStepTotalGrossWeight)
      setFieldValue(
        `productionSteps[${indexStep}].stepComponents[${indexComponent}]`,
        newStepComponent
      )
    }
  }

  const handleChangeTransformationMode = (event) => {
    const transformationMode = transformationModesOptions.find(option => option.transformationMode.objectId === event.target.value)

    const newParentStep = cloneDeep(parentStep)

    let newStepComponent = {
      ...stepComponent,
      transformationMode: transformationMode.transformationMode,
      transformRate: transformationMode.transformationRate,
    }

    newStepComponent = recalculateCostValues(newStepComponent) // the change of grossweight is done here
    // update step component
    newParentStep.stepComponents[indexComponent] = newStepComponent

    if (fromRecipe) {
      // update parent step and steps
      const index = steps.findIndex(step => isEqual(step.step, parentStep))
      steps[index].step = newParentStep
      // update step form values
      setFieldValue(`sections[${sectionIndex}].productionSteps[${indexStep}].step`, newParentStep)
    }
    else {
      // update parent step and steps
      const index = steps.indexOf(parentStep)
      steps[index] = newParentStep
      // update step form values
      setFieldValue(`productionSteps[${indexStep}]`, newParentStep)
    }

    updateAllRecipesFieldsOnChange({
      recipe,
      section,
      steps,
      parentStep,
      currentStepComponentChanged: newStepComponent,
      isProductionSteps: !!fromRecipe
    })
  }

  const stepComponentArticleFormName = useMemo(() => {
    if (fromRecipe) {
      return stepComponent.sectionOutput
        ? `sections[${sectionIndex}].productionSteps[${indexStep}].step.stepComponents[${indexComponent}].sectionOutput`
        : stepComponent.supplierItem
          ? `sections[${sectionIndex}].productionSteps[${indexStep}].step.stepComponents[${indexComponent}].supplierItem`
          : `sections[${sectionIndex}].productionSteps[${indexStep}].step.stepComponents[${indexComponent}].priorSteps`
    }
    return stepComponent.sectionOutput
      ? `productionSteps[${indexStep}].stepComponents[${indexComponent}].sectionOutput`
      : stepComponent.supplierItem
        ? `productionSteps[${indexStep}].stepComponents[${indexComponent}].supplierItem`
        : `productionSteps[${indexStep}].stepComponents[${indexComponent}].priorSteps`
  }, [fromRecipe, indexStep, indexComponent, sectionIndex, stepComponent, stepComponent.supplierItem, stepComponent.sectionOutput])

  const stepComponentTransformationModeName = useMemo(() => {
    return fromRecipe
      ? `sections[${sectionIndex}].productionSteps[${indexStep}].step.stepComponents[${indexComponent}].transformationMode`
      : `productionSteps[${indexStep}].stepComponents[${indexComponent}].transformationMode`
  }, [fromRecipe, sectionIndex, indexComponent, indexStep])

  const stepComponentTransformRateName = useMemo(() => {
    return fromRecipe
      ? `sections[${sectionIndex}].productionSteps[${indexStep}].step.stepComponents[${indexComponent}].transformRate`
      : `productionSteps[${indexStep}].stepComponents[${indexComponent}].transformRate`
  }, [fromRecipe, sectionIndex, indexComponent, indexStep])

  const netWeightName = useMemo(() => {
    return fromRecipe
      ? `sections[${sectionIndex}].productionSteps[${indexStep}].step.stepComponents[${indexComponent}].netWeight`
      : `productionSteps[${indexStep}].stepComponents[${indexComponent}].netWeight`
  }, [fromRecipe, sectionIndex, indexComponent, indexStep])

  // Retrieve stepComponents with a piece unit supplier item
  const supplierItemPieceUnitWeight = getSupplierItemPieceUnitWeight(stepComponent, supplierItem)

  return (
    <Box
      sx={{
        display: "flex"
      }}
      onClick={_stopPropagation}
    >
      <StyledStepFirstBodyColumn className="flexRow center" style={{ backgroundColor: COLORS.PRODUCTION_STEPS_COMPONENT_WHITE }} leftStep={COLS_OFFSETS.stepComponents}>
        {isHover && (
          <Button onClick={(e) => _addStepComponent(indexComponent, e)} className="flexCenter" sx={{ position: "absolute", left: -8 }}>
            <img alt="plus icon" src="/icons/plus-blue.svg" />
          </Button>
        )}
        <Stack direction="column" spacing={1} sx={{ flex: 1 }}>
          <Stack direction="column" spacing={1} sx={{ flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Field
                name={stepComponentArticleFormName}
                loading={loading}
                component={FormikAutocomplete}
                options={supplierItemsOptions}
                isHovered={isHover}
                forcePopupIcon={isHover}
                supplierItems={supplierItems}
                stepComponent={stepComponent}
                isOptionEqualToValue={(option, value) => value && option.value === value.value}
                priorSteps={priorSteps}
                priorSectionOutputs={priorSectionOutputs}
                getOptionLabel={(option) => getOptionLabel(option, stepNumbers)}
                onChange={(_, newValue) => handleChangeSupplierItemOrPriorStepAutocomplete(newValue)} //, indexComponent)} KFC-2429 - To be added 
                onInputChange={searchingSupplier}
                category={stepComponent.sectionOutput ? "SECTION_OUTPUTS" : stepComponent.supplierItem ? "SUPPLIER_ITEM" : "PRIOR_STEPS"}
                renderOption={(props, option) =>
                (
                  <li {...props} key={`parent-${parentStep && parentStep.objectId}-${sectionIndex}-${indexStep}-${indexComponent}-${stepComponent.priorSteps && stepComponent.priorSteps.objectId}-${option.value}`}>
                    {option.name}
                  </li>
                )
                }
                disableClearable
                key={`parent-${parentStep ? parentStep.objectId : ""}-${sectionIndex}-step-${indexStep}-component-${indexComponent}-${stepComponent.priorSteps && stepComponent.priorSteps.objectId}`}
              />
              <ErrorMessage
                name={`sections[${sectionIndex}].productionSteps[${indexStep}].step.stepComponents[${indexComponent}].supplierItem`}
                render={(message) => (
                  <StyledErrorMessage>{message}</StyledErrorMessage>
                )}
              />
              <ErrorMessage
                name={`sections[${sectionIndex}].productionSteps[${indexStep}].step.stepComponents[${indexComponent}].priorSteps`}
                render={(message) => (
                  <StyledErrorMessage>{message}</StyledErrorMessage>
                )}
              />
              <ErrorMessage
                name={`sections[${sectionIndex}].productionSteps[${indexStep}].step.stepComponents[${indexComponent}].sectionOutput`}
                render={(message) => (
                  <StyledErrorMessage>{message}</StyledErrorMessage>
                )}
              />
            </Stack>
            <ErrorMessage
              name={`sections[${sectionIndex}].productionSteps[${indexComponent}].step.name`}
              render={(message) => (
                <StyledErrorMessage>{message}</StyledErrorMessage>
              )}
            />
          </Stack>
        </Stack>
        {/* error message while not on hover */}
        {!isHover && hasError(indexComponent, "description") && (
          <ErrorMessage
            name={`sections[${sectionIndex}].productionSteps[${indexComponent}].step.description`}
            render={(message) => (
              <StyledErrorMessage>{message}</StyledErrorMessage>
            )}
          />
        )}
      </StyledStepFirstBodyColumn>
      <StyledStepBodyCell align="left" width={widths[1]}>
        <Stack direction="column" spacing={"4px"} padding="16px 0">
          <StyledStepText weight={0}>{stepComponent.grossWeight ? roundNumber(convertKilosIntoGrams(stepComponent.grossWeight), 3) : "-"}</StyledStepText>
          <StyledPieceStepInfo>
            {supplierItemPieceUnitWeight ? `=${roundNumber(supplierItemPieceUnitWeight, 2).toString().replace(".", ",")} ${roundNumber(supplierItemPieceUnitWeight, 2) > 1 ? "pièces" : "pièce"}` : ""}
          </StyledPieceStepInfo>
        </Stack>
      </StyledStepBodyCell>
      <StyledStepBodyCell align="left" width={widths[2]}>
        <StyledStepText weight={0}>{supplierItem && supplierItem.pricePerKg ? roundNumber(supplierItem.pricePerKg, 2) + " €" : "-"}</StyledStepText>
      </StyledStepBodyCell>
      <StyledStepBodyCell align="left" width={widths[3]}>
        <StyledStepText weight={0}>
          {stepComponent.realCost || stepComponent.realCost === 0 ? `${roundNumber(stepComponent.realCost, 3)} €` : "-"}
        </StyledStepText>
      </StyledStepBodyCell>
      <StyledStepBodyCell align="left" width={widths[4]}>
        <StyledStepText weight={0}>
          {transformationModesOptions.length > 0 && isHover ? (
            <Stack className="flex1">
              <Field
                name={stepComponentTransformationModeName}
                component={FormikEditableStepSelect}
                stepComponent={stepComponent}
                transformationModesOptions={transformationModesOptions}
                isClearable={true}
                endAdornment={<IconButton sx={{ display: stepComponent.transformationMode ? "" : "none" }} onClick={() => clearStepComponentTransformationMode()}><ClearIcon /></IconButton>}
                onChange={handleChangeTransformationMode}
              >
                {transformationModesOptions && transformationModesOptions.map((transformation, keyIndex) => (
                  <MenuItem isClearable={true} key={indexStep + "-" + indexComponent + "-" + keyIndex + "-" + transformation.transformationMode.objectId} value={transformation.transformationMode.objectId}>
                    {transformation.transformationMode.name}
                  </MenuItem>
                ))}
              </Field>
              <ErrorMessage
                name={stepComponentTransformationModeName}
                render={(message) => (
                  <StyledErrorMessage>{message}</StyledErrorMessage>
                )}
              />
            </Stack>
          )
            :
            (
              <StyledStepText weight={0}>
                {stepComponent.transformationMode && stepComponent.transformationMode.name ? stepComponent.transformationMode.name : "-"}
              </StyledStepText>
            )
          }
        </StyledStepText>
      </StyledStepBodyCell>
      <StyledStepBodyCell align="left" width={widths[6]}>
        <StyledStepText weight={0}>{supplierItem && (stepComponent.transformRate || stepComponent.transformRate === 0) ? stepComponent.transformRate + " %" : "100 %"}</StyledStepText>
      </StyledStepBodyCell>
      <StyledStepBodyCell align="left" width={widths[5]}>
        {supplierItem && isHover ?
          (
            <Stack>
              <Field
                component={FormikTextField}
                name={netWeightName}
                onClick={_stopPropagation}
                onChange={handleChangeNetWeight}
                value={isHover ? convertKilosIntoGrams(stepComponent.netWeight) : (stepComponent.netWeight) || 0}
                onWheel={(e) => e.target.blur()} // prevent increase and decrease on mouse wheel
                type="number"
              />
              <ErrorMessage
                name={netWeightName}
                render={(message) => (
                  <StyledErrorMessage>{message}</StyledErrorMessage>
                )}
              />
            </Stack>
          )
          :
          <StyledStepText weight={0}>{stepComponent.netWeight ? roundNumber(convertKilosIntoGrams(stepComponent.netWeight), 3) : "-"}</StyledStepText>
        }
      </StyledStepBodyCell>
      {/* ------------ kitchenArea ------------ */}
      <StyledStepBodyCell align="left" width={widths[7]}>
        <StyledStepText>{"-"}</StyledStepText>
      </StyledStepBodyCell>
      <StyledStepBodyCell align="left" width={widths[8]}>
        <StyledStepText>{"-"}</StyledStepText>
      </StyledStepBodyCell>
      <StyledStepBodyCell align="left" width={widths[9]}>
        <StyledStepText>{"-"}</StyledStepText>
      </StyledStepBodyCell>
      {/* ------------ stepDuration ------------ */}
      <StyledStepBodyCell align="left" width={widths[10]}>
        <StyledStepText>{"-"}</StyledStepText>
      </StyledStepBodyCell>
      {/* ------------ stepDurationUnit ------------ */}
      <StyledStepBodyCell align="left" width={widths[11]}>
        <StyledStepText>{"-"}</StyledStepText>
      </StyledStepBodyCell>
      {/* ------------ end step weighing ------------ */}
      <StyledStepBodyCell align="left" width={widths[12]}>
        <StyledStepText>{"-"}</StyledStepText>
      </StyledStepBodyCell>
      {/* ------------ end step weighing ------------ */}
      <StyledStepBodyCell align="left" width={widths[13]}>
        <StyledStepText>{"-"}</StyledStepText>
      </StyledStepBodyCell>
      {/* -------- delete icon -------- */}
      <RemoveColumn
        type="stepComponent"
        isHover={isHover}
        onClick={(e) => _removeStepComponent(indexComponent, e)}
      />
    </Box>
  )
}

export default EditableStepComponent
